import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Grid,
  Avatar,
  Tabs,
  Tab,
  Chip,
  Divider,
} from "@mui/material";
import { getProduct } from "../../../../services/Admin/ProductsApi";
import { getImageUrl } from "../../../../services/indexMediaBaseUrl";
import ProductImagesTab from "./ProductDetailsComponents/ProductImagesTab";
import ProductSizesTab from "./ProductDetailsComponents/ProductSizesTab";
import ProductPricesTab from "./ProductDetailsComponents/ProductPricesTab";
import ProductFeaturesTab from "./ProductDetailsComponents/ProductFeaturesTab";
import ProductColorsTab from "./ProductDetailsComponents/ProductColorsTab";

const ProductDetailsDialog = ({ open, onClose, productId }) => {
  const [loading, setLoading] = useState(false);
  const [product, setProduct] = useState(null);
  const [error, setError] = useState(null);

  // new: tab state (0..4)
  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    if (!open) {
      setProduct(null);
      setError(null);
      setLoading(false);
      setTabIndex(0);
      return;
    }
    if (!productId) return;
    const fetch = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getProduct(productId);
        const data = res && res.success ? res.data : res;
        setProduct(data);
      } catch (err) {
        setError(err?.message || "Error loading product");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [open, productId]);

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const handleTabChange = (e, v) => setTabIndex(v);

  return (
    <Dialog
      open={open}
      onClose={() => !loading && onClose && onClose()}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: "80vh",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle>تفاصيل المنتج</DialogTitle>
      <DialogContent sx={{ height: "calc(80vh - 64px)", overflow: "auto" }}>
        {loading ? (
          <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ py: 2 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : product ? (
          <Box sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              {/* left: tabs and content */}
              <Grid item xs={12} md={8}>
                <Tabs value={tabIndex} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" sx={{ mb: 2 }}>
                  <Tab label="صور المنتج" />
                  <Tab label="مقاسات وأحجام المنتج" />
                  <Tab label="أسعار المنتج" />
                  <Tab label="مميزات ووصف المنتج" />
                  <Tab label="ألوان المنتج" />
                </Tabs>

                {/* content area: replaced with dedicated tab components */}
                <Box>
                  {tabIndex === 0 && (
                    <ProductImagesTab
                      images={product.images || []}
                      productId={product.id}
                      onUploaded={async () => {
                        // reload product after successful upload
                        try {
                          setLoading(true);
                          const res = await getProduct(productId);
                          const data = res && res.success ? res.data : res;
                          setProduct(data);
                        } catch (err) {
                          // ignore reload errors here
                        } finally {
                          setLoading(false);
                        }
                      }}
                    />
                  )}
                  {tabIndex === 1 && (
                    <ProductSizesTab
                      sizes={product.sizes || []}
                      productId={product.id}
                      onSuccess={async () => {
                        try {
                          setLoading(true);
                          const res = await getProduct(productId);
                          const data = res && res.success ? res.data : res;
                          setProduct(data);
                        } catch (err) {
                          // ignore reload errors
                        } finally {
                          setLoading(false);
                        }
                      }}
                    />
                  )}
                  {tabIndex === 2 && (
                    <ProductPricesTab productId={product.id} currencies={product.currencies || []} />
                  )}
                  {tabIndex === 3 && (
                    <ProductFeaturesTab
                      productId={product.id}
                      desc={product.desc_ar || product.desc_en}
                      features={product.features || []}
                      onSuccess={async () => {
                        try {
                          setLoading(true);
                          const res = await getProduct(productId);
                          const data = res && res.success ? res.data : res;
                          setProduct(data);
                        } catch (err) {
                          // ignore reload errors
                        } finally {
                          setLoading(false);
                        }
                      }}
                    />
                  )}
                  {tabIndex === 4 && (
                    <ProductColorsTab
                      productId={product.id}
                      colors={product.colors || []}
                      onSuccess={async () => {
                        try {
                          setLoading(true);
                          const res = await getProduct(productId);
                          const data = res && res.success ? res.data : res;
                          setProduct(data);
                        } catch (err) {
                          // ignore reload errors
                        } finally {
                          setLoading(false);
                        }
                      }}
                    />
                  )}
                </Box>
              </Grid>

              {/* right: product info */}
              <Grid item xs={12} md={4}>
                <Box sx={{ mb: 2 }}>
                  <Avatar
                    variant="rounded"
                    src={getImageUrl(product.main_photo)}
                    alt={product.name_ar || product.name_en}
                    sx={{ width: "100%", height: 320, bgcolor: "#f5f5f5" }}
                  />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {/* الاسم */}
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {product.name_ar}
                    </Typography>
                    {product.name_en && (
                      <Typography variant="body2" color="text.secondary">
                        {product.name_en}
                      </Typography>
                    )}
                  </Box>

                  <Divider />

                  {/* الفئة */}
                  {product.categroy && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="body2" color="text.secondary">الفئة</Typography>
                      <Chip label={product.categroy.name_ar || product.categroy.name_en} size="small" variant="outlined" />
                    </Box>
                  )}

                  {/* الوصف */}
                  {(product.desc_ar || product.desc_en) && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>الوصف</Typography>
                      {product.desc_ar && <Typography variant="body2">{product.desc_ar}</Typography>}
                      {product.desc_en && product.desc_en !== product.desc_ar && (
                        <Typography variant="body2" color="text.secondary">{product.desc_en}</Typography>
                      )}
                    </Box>
                  )}

                  <Divider />

                  {/* الكمية */}
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" color="text.secondary">الكمية</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{product.quantity ?? 0}</Typography>
                  </Box>

                  {/* الخصم */}
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" color="text.secondary">الخصم</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{product.discount ?? 0}</Typography>
                  </Box>

                  {/* الحالة */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="body2" color="text.secondary">الحالة</Typography>
                    <Chip
                      label={product.status ? "مفعّل" : "غير مفعّل"}
                      size="small"
                      color={product.status ? "success" : "default"}
                    />
                  </Box>

                  {/* التخفيض */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="body2" color="text.secondary">تخفيض</Typography>
                    <Chip
                      label={product.sale ? "نعم" : "لا"}
                      size="small"
                      color={product.sale ? "warning" : "default"}
                    />
                  </Box>

                  {/* حالة الإرجاع */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="body2" color="text.secondary">حالة الإرجاع</Typography>
                    <Chip
                      label={product.retrun_status ? "مسموح" : "غير مسموح"}
                      size="small"
                      color={product.retrun_status ? "info" : "default"}
                    />
                  </Box>

                  {/* سياسة الإرجاع */}
                  {(product.return_policie_ar || product.return_policie_en) && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>سياسة الإرجاع</Typography>
                      {product.return_policie_ar && <Typography variant="body2">{product.return_policie_ar}</Typography>}
                      {product.return_policie_en && product.return_policie_en !== product.return_policie_ar && (
                        <Typography variant="body2" color="text.secondary">{product.return_policie_en}</Typography>
                      )}
                    </Box>
                  )}

                  <Divider />

                  {/* الأحجام */}
                  {product.sizes && product.sizes.length > 0 && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>الأحجام</Typography>
                      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        {product.sizes.map((s) => (
                          <Chip key={s.id} label={`${s.size} (${s.key})`} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* الألوان */}
                  {product.colors && product.colors.length > 0 && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>الألوان</Typography>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        {product.colors.map((c) => (
                          <Chip
                            key={c.id}
                            label={c.color_ar || c.color_en}
                            size="small"
                            variant="outlined"
                            avatar={
                              <Box
                                sx={{
                                  width: 16,
                                  height: 16,
                                  borderRadius: "50%",
                                  bgcolor: c.code || "#ccc",
                                  border: "1px solid #ddd",
                                  ml: "4px !important",
                                }}
                              />
                            }
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* الأسعار */}
                  {product.currencies && product.currencies.length > 0 && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>الأسعار</Typography>
                      {product.currencies.map((cur) => (
                        <Box key={cur.id} sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                          <Typography variant="caption">دولة #{cur.country_id}</Typography>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>{cur.price}</Typography>
                            {cur.price_after_discount && cur.price_after_discount !== cur.price && (
                              <Typography variant="caption" color="success.main">← {cur.price_after_discount}</Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* المميزات */}
                  {product.features && product.features.length > 0 && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>المميزات</Typography>
                      {product.features.map((f) => (
                        <Box key={f.id} sx={{ mb: 0.5, p: 1, bgcolor: "#f9f9f9", borderRadius: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{f.title_ar || f.title_en}</Typography>
                          <Typography variant="caption" color="text.secondary">{f.desc_ar || f.desc_en}</Typography>
                        </Box>
                      ))}
                    </Box>
                  )}

                  <Divider />

                  {/* التواريخ */}
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      تاريخ الإنشاء: {formatDate(product.created_at)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      آخر تحديث: {formatDate(product.updated_at)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <Box sx={{ py: 2 }}>
            <Typography>لا توجد بيانات</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => !loading && onClose && onClose()}>إغلاق</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductDetailsDialog;
